import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Store } from 'react-notifications-component';
import { isArray, isEqual, isNull, toString } from 'lodash';

import { Button } from 'react-bootstrap';
import { getErrorMessage } from '../../../utils/helper';
import { defaultNotificationOptions } from '../../../utils/config';

import { Dropdown, useDropdown } from '../../Dropdown';
import { List } from '../../List';
import { FilterRow } from '../index';
import { useFilters } from '../../../providers/Filters/useFilters';
import { Badge as SkeletonBadge } from '../../Skeleton';
import { Flex } from '../../Layout';

const FilterMode = ({ isDataUpdated, filterData, onChangeFilterData, filterMethod, updateService }) => {
  const cancelSourceRef = useRef(null);
  const timeoutRef = useRef(null);

  const [filterSelected, setSelectedFilter] = useState([]);

  const { close } = useDropdown();

  const { filters, onChangeFilter } = useFilters();
  const filter = filters[filterMethod]

  const onSelectedFilter = (id) => {
    if (id === null) return;

    const numId = Number(id);

    setSelectedFilter((prevState) => {
      const filteredState = prevState.filter((item) => Number(item) !== numId);
      const hadElements = prevState.some((item) => Number(item) === numId);

      return hadElements ? filteredState : [...filteredState, id];
    });
  }

  const onSubmitFilter = (e) => {
    e.preventDefault();
    try {
      if (!filter.modes && !filterSelected.length) {
        return;
      }

      const filterSelectedString = filterSelected.join(',');

      if (!isEqual(filter.modes, filterSelectedString)) {
        onChangeFilter({
          [filterMethod]: {
            modes: filterSelectedString,
          },
        })
      }
    } finally {
      close();
    }
  }

  const updateData = async (currentQueryFilters) => {
    try {
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel('Request canceled');
      }

      const source = axios.CancelToken.source();
      cancelSourceRef.current = source;

      const response = await updateService(currentQueryFilters, source.token);

      // Response с очередью
      if (response.data.hasOwnProperty('queue')) {
        if (response.data.hasOwnProperty('items') && isNull(response.data?.queue?.id)) {
          onChangeFilterData(response.data.items);
        } else {
          throw new Error('Invalid items format')
        }

        if (response.data.queue.id !== null) {
          const timeoutId = setTimeout(() => {
            updateData({
              ...currentQueryFilters,
              queue_id: response.data.queue.id,
            });
          }, response.data.queue.timeout);

          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = timeoutId;
        }
        // Response сразу с data
      } else if (isArray(response.data?.data)) {
        onChangeFilterData(response.data.data);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        const errorMessage = getErrorMessage(error);
        Store.addNotification({
          ...defaultNotificationOptions,
          title: 'Mode',
          message: errorMessage,
        });
      }
    }
  };

  useEffect(() => {
    if (!isDataUpdated && updateService) {
      updateData(filter);
    }

    return () => {
      // Очистка при размонтировании
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel('Component unmounted');
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filter.hasOwnProperty('modes')) {
      setSelectedFilter([]);
    } else {
      const modesToString = toString(filter.modes);
      setSelectedFilter(modesToString.split(','));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.modes])

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Enter' || event.keyCode === 13) {
        onSubmitFilter(event);
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSelected]);

  return (
    <>
      <Dropdown.Body>
        <List
          maxHeight={210}
          data={filterData}
          rowRender={({ item, index }) => (
            <FilterRow
              showCheckbox
              key={`filter-${index}`}
              onClick={() => onSelectedFilter(item?.id)}
              isActive={
                item.id !== null
                  && filterSelected.some((filterId) => Number(filterId) === Number(item.id))
              }
            >
              <Flex
                gap={4}
                align="center"
                wrap="wrap"
              >
                {item.label}
                {!isDataUpdated && updateService ? <SkeletonBadge height={16} width={18} /> : !isNull(item.count) ? (
                  <span>
                    {' '}
                    (
                    {item.count}
                    )
                  </span>
                ) : null}
              </Flex>
            </FilterRow>
          )}
        />
      </Dropdown.Body>
      <Dropdown.Footer p={16}>
        <Button
          block
          bsStyle="primary"
          onClick={onSubmitFilter}
        >
          Apply
        </Button>
      </Dropdown.Footer>
    </>
  );
}

FilterMode.defaultProps = {
  isDataUpdated: false,
};

FilterMode.propTypes = {
  isDataUpdated: PropTypes.bool,
  filterData: PropTypes.array,
  onChangeFilterData: PropTypes.func,
  filterMethod: PropTypes.string,
  updateService: PropTypes.func,
};

export default memo(FilterMode);
